import {
  buffers, eventChannel,
} from 'redux-saga';
import {
  call, delay, put, race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import {
  registerWMSOptimizedTerritoriesBroadcastListener,
  type WMSCanceledOptimizedTerritoriesEventData,
  type WMSFailedOptimizedTerritoriesEventData,
  type WMSOptimizedTerritoriesEventData,
  type WMSOptimizedTerritoriesStatusEventData,
} from '~/_shared/utils/api/broadcast.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  BoundaryCreateWMSOptimizedStatusResponse,
  getMapData, type MapInfoResponse,
} from '~/map/map.repository';
import { ModalType } from '~/modal/modalType.enum';
import { handleOptimizedTerritoriesCreateSuccess } from '~/store/frontendState/mapTools/boundary/boundaryCreateOptimized/boundaryCreateOptimized.sagas';
import type { BoundaryCreateWMSOptimizedAction } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.action';
import { BOUNDARY_CREATE_WMS_OPTIMIZED_ADD_ITEM } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.actionTypes';
import { boundaryCreateWMSOptimizedPendingItemsSelector } from '~/store/frontendState/mapTools/boundary/boundaryCreateWMSOptimized/boundaryCreateWMSOptimized.selectors';
import type { MapIdState } from '~/store/mapId/mapId.state';
import { type MapInfoAction } from '~/store/mapInfo/mapInfo.action';
import { MAP_INFO_FETCH_DATA_SUCCESS } from '~/store/mapInfo/mapInfo.actionTypes';
import {
  createAppError, openModal,
} from '~/store/modal/modal.actionCreators';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '~/store/userData/userData.actionTypes';
import { translate } from '~/translations/Trans';
import {
  boundaryCreateWMSOptimizedAddItem, boundaryCreateWMSOptimizedRemoveItem, boundaryCreateWMSOptimizedUpdateItem,
} from './boundaryCreateWMSOptimized.actionCreators';
import {
  type BoundaryCreateWMSOptimizedItem, BoundaryCreateWMSOptimizedStatus,
} from './boundaryCreateWMSOptimized.state';
import { createBoundaryCreateWMSOptimizedStatusFromResponse } from './boundaryCreateWMSOptimizedStatus.factory';

const POLLING_DELAY = 60_000;

type OptimizedTerritoriesListenerMessage = {
  type: 'change';
  data: WMSOptimizedTerritoriesStatusEventData;
} | {
  type: 'success';
  data: WMSOptimizedTerritoriesEventData;
} | {
  type: 'error';
  data: WMSFailedOptimizedTerritoriesEventData;
} | {
  type: 'cancel';
  data: WMSCanceledOptimizedTerritoriesEventData;
};

const itemChannelEmitters = new Map<number, Array<(message: OptimizedTerritoriesListenerMessage) => void>>();

const registerWMSOptimizedTerritoriesListener = () => {
  return eventChannel<OptimizedTerritoriesListenerMessage>(emitter => {
    registerWMSOptimizedTerritoriesBroadcastListener({
      onStatusChange: (data) => emitter({ type: 'change', data }),
      onSuccess: (data) => emitter({ type: 'success', data }),
      onError: (data) => emitter({ type: 'error', data }),
      onCancel: (data) => emitter({ type: 'cancel', data }),
    });
    return noop;
  }, buffers.expanding());
};

const registerProgressItemChannel = (item: BoundaryCreateWMSOptimizedItem) => {
  // Create a dedicated channel for this specific item that will receive broadcasted messages
  return eventChannel<OptimizedTerritoriesListenerMessage>((emitterFn) => {
    // Register this emitter to receive messages for this item's id
    if (!itemChannelEmitters.has(item.id)) {
      itemChannelEmitters.set(item.id, []);
    }
    const emitters = itemChannelEmitters.get(item.id);
    if (emitters) {
      emitters.push(emitterFn);
    }

    return () => {
      // Cleanup: remove this emitter from the registry
      const emitters = itemChannelEmitters.get(item.id);
      if (emitters) {
        const index = emitters.indexOf(emitterFn);
        if (index > -1) {
          emitters.splice(index, 1);
        }
        // Clean up the map entry if no more emitters for this id
        if (emitters.length === 0) {
          itemChannelEmitters.delete(item.id);
        }
      }
    };
  }, buffers.expanding());
};

export function* boundaryCreateWMSOptimizedSagas() {
  yield takeLatest(MAP_INFO_FETCH_DATA_SUCCESS, onMapInfoFetchSuccess);
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, initializeWebSocketChannel);
  yield takeEvery(BOUNDARY_CREATE_WMS_OPTIMIZED_ADD_ITEM, onOptimizedTerritoryAdded);
}

function* onMapInfoFetchSuccess(action: PickAction<MapInfoAction, typeof MAP_INFO_FETCH_DATA_SUCCESS>) {
  const mapInfo = action.payload.data;
  for (const territoryGeneration of mapInfo.territoryGeneration.filter(item => item.type === 'WMS')) {
    if (territoryGeneration.status !== BoundaryCreateWMSOptimizedStatus.Finished) {
      yield put(boundaryCreateWMSOptimizedAddItem(territoryGeneration));
    }
  }
  if (mapInfo.importing || mapInfo.spreadsheets.some(spreadsheetInfo => spreadsheetInfo.importing)) {
    yield put(openModal(ModalType.ImportingSpreadsheet));
  }
}

function* initializeWebSocketChannel() {
  const sharedChannel = registerWMSOptimizedTerritoriesListener();

  try {
    // Start the broadcaster saga that will distribute messages to all item channels
    yield call(broadcastMessagesToItemChannels, sharedChannel);
  }
  finally {
    // Clean up the shared channel when the saga ends or is cancelled
    if (sharedChannel) {
      sharedChannel.close();
    }
  }
}

// Broadcaster saga: reads from sharedChannel once and distributes to all item-specific channels
function* broadcastMessagesToItemChannels(sharedChannel: ReturnType<typeof registerWMSOptimizedTerritoriesListener>) {
  while (true) {
    if (!sharedChannel) {
      break;
    }

    const message: OptimizedTerritoriesListenerMessage = yield take(sharedChannel);

    // Get the id from the message
    const id = message.data?.id;
    if (!id) {
      continue;
    }

    // Broadcast to all emitters registered for this id
    const emitters = itemChannelEmitters.get(id);
    if (emitters) {
      // Send message to all listeners for this id
      emitters.forEach(emitter => emitter(message));
    }
  }
}

function* onOptimizedTerritoryAdded(action: PickAction<BoundaryCreateWMSOptimizedAction, typeof BOUNDARY_CREATE_WMS_OPTIMIZED_ADD_ITEM>) {
  yield call(watchOptimizationProgressSaga, action.payload.item);
}

function* watchOptimizationProgressSaga(item: BoundaryCreateWMSOptimizedItem) {
  const itemChannel = registerProgressItemChannel(item);

  try {
    while (true) {
      if (!itemChannel) {
        break;
      }

      const { message }: {message: OptimizedTerritoriesListenerMessage | undefined} = yield race({
        message: take(itemChannel),
        timeout: delay(POLLING_DELAY),
      });

      if (message) {
        switch (message.type) {
          case 'change': {
            yield put(boundaryCreateWMSOptimizedUpdateItem({
              type: 'WMS',
              id: message.data.id,
              status: createBoundaryCreateWMSOptimizedStatusFromResponse(message.data.status),
              isFailed: false,
            }));
            continue;
          }
          case 'success': {
            yield put(boundaryCreateWMSOptimizedRemoveItem(message.data.id));
            yield call(handleOptimizedTerritoriesCreateSuccess, {
              startedOnCurrentTab: item.startedOnCurrentTab,
              boundaryGroupId: message.data.btg.boundary_group_id,
            });
            break;
          }
          case 'cancel': {
            yield put(boundaryCreateWMSOptimizedRemoveItem(message.data.id));
            break;
          }
          case 'error': {
            yield put(boundaryCreateWMSOptimizedRemoveItem(message.data.id));
            yield put(createAppError({
              type: AppErrorType.General,
              title: translate('Failed to create optimised territories'),
            }));
            break;
          }
          default:
        }
      }
      else {
        const clientId: number | null = yield select<number | null>(clientIdSelector);
        const mapId: MapIdState = yield select<MapIdState>(mapIdSelector);

        if (mapId === null || clientId === null) {
          break;
        }

        const response: { data: MapInfoResponse } = yield call(getMapData, clientId, mapId);
        const mapInfoTerritoryItem = response.data.territories_generation?.filter(pendingItem => pendingItem.type === 'WMS').find(pendingItem => pendingItem.id === item.id);

        const boundaryCreateOptimizedWMSPendingItems: ReadonlyArray<BoundaryCreateWMSOptimizedItem>
          = yield select<ReadonlyArray<BoundaryCreateWMSOptimizedItem>>(boundaryCreateWMSOptimizedPendingItemsSelector);

        const pendingItem = boundaryCreateOptimizedWMSPendingItems.find(optimizedItem => optimizedItem.id === item.id);

        if (!pendingItem) {
          break;
        }

        if (!mapInfoTerritoryItem || mapInfoTerritoryItem.status === BoundaryCreateWMSOptimizedStatusResponse.Finished) {
          yield put(boundaryCreateWMSOptimizedRemoveItem(item.id));
          yield call(handleOptimizedTerritoriesCreateSuccess, {
            startedOnCurrentTab: item.startedOnCurrentTab,
          });
          break;
        }
        else {
          yield put(boundaryCreateWMSOptimizedUpdateItem({
            ...item,
            status: createBoundaryCreateWMSOptimizedStatusFromResponse(mapInfoTerritoryItem.status),
          }));
        }
      }
    }
  }
  finally {
    // Clean up the item-specific channel when the saga ends
    if (itemChannel) {
      itemChannel.close();
    }
  }
}
